/**
 * Hub Types
 *
 * App-owned hub structure with ordered sections.
 * WordPress is used only for individual resource posts.
 */

export type HubSectionType = 'CONTENT' | 'RESOURCE_GROUP';

export type HubStatus = 'DRAFT' | 'PUBLISHED';

export interface Hub {
  id: string;
  wpPostId?: number | null;
  title: string;
  slug: string;
  description?: string | null;
  status: HubStatus;
  featured: boolean;
  sortOrder: number;
  interestTopics?: string[];
  /** Set when this toolkit is assigned to a subject. Null means it stands alone. */
  programId: string | null;
  /** Published program this hub belongs to. Null when unassigned or the program is still a draft. */
  program?: {
    id: string;
    title: string;
    slug: string;
  } | null;
  /** Published hubs in the same program. Present on the single-hub response. */
  programHubs?: Array<{
    title: string;
    slug: string;
    sortOrder: number;
    resourceCount: number;
  }>;

  resources: HubResource[];
  sections: HubSection[];

  createdAt: string;
  updatedAt: string;
}

export interface HubSection {
  id: string;
  hubId: string;
  type: HubSectionType;
  title: string;
  body?: string | null;
  description?: string | null;
  order: number;
  resources?: HubResource[];

  createdAt: string;
  updatedAt: string;
}

export interface HubResource {
  id: string;
  hubId: string;
  wpPostId: number;
  sectionId?: string | null;
  section?: HubSection | null;
  isHighlighted: boolean;
  order: number;

  createdAt: string;
}

/** @deprecated Kept for transitional WP fallback on public detail */
export interface HubWithContent extends Hub {
  wpContent?: {
    content: string;
    excerpt: string;
    featuredMedia?: number;
    featuredMediaUrl?: string;
  };
}

export interface CreateHubInput {
  title: string;
  slug: string;
  description?: string;
  wpPostId?: number | null;
  status?: HubStatus;
  featured?: boolean;
}

export interface UpdateHubInput {
  title?: string;
  slug?: string;
  description?: string;
  status?: HubStatus;
  featured?: boolean;
}

export function selectFeaturedHubs(hubs: Hub[], limit = 3): Hub[] {
  return hubs
    .filter((hub) => hub.featured && hub.resources.length > 0)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, limit);
}

export interface AddResourceToHubInput {
  wpPostId: number;
  sectionId?: string | null;
  order?: number;
  isHighlighted?: boolean;
}

export interface UpdateHubResourceInput {
  sectionId?: string | null;
  order?: number;
  isHighlighted?: boolean;
}

export interface CreateSectionInput {
  type: HubSectionType;
  title: string;
  body?: string;
  description?: string;
  order?: number;
}

export interface UpdateSectionInput {
  title?: string;
  body?: string | null;
  description?: string | null;
  order?: number;
}

export interface ReorderSectionsInput {
  sectionIds: string[];
}
